import { useState, useEffect } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const location = useLocation()

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false)
  }

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-white'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <NavLink
            to="/"
            className="flex items-center gap-3 group"
            onClick={(e) => {
              closeMobileMenu()
              if (location.pathname === '/') {
                e.preventDefault()
                window.scrollTo({ top: 0, behavior: 'smooth' })
              }
            }}
          >
            <div className="relative">
              <img
                src="/images/logo/bakenook-logo.jpeg"
                alt="The BakeNook Logo"
                className="h-12 w-auto object-contain transform transition-transform duration-500 group-hover:scale-110"
              />
              {/* Subtle glow effect on hover */}
              <div className="absolute inset-0 bg-coral-500 rounded-full blur-xl opacity-0 group-hover:opacity-20 transition-opacity duration-500" />
            </div>
            <span className="text-2xl font-heading font-bold text-charcoal tracking-tight group-hover:text-coral-500 transition-colors duration-300">
              The BakeNook
            </span>
          </NavLink>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <NavLink
              to="/"
              className={({ isActive }) =>
                `relative text-base font-medium transition-colors duration-300 ${
                  isActive
                    ? 'text-coral-500'
                    : 'text-gray-700 hover:text-coral-500'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  Home
                  {isActive && (
                    <span className="absolute -bottom-2 left-0 right-0 h-0.5 bg-coral-500 rounded-full animate-slideIn" />
                  )}
                </>
              )}
            </NavLink>

            <NavLink
              to="/about"
              className={({ isActive }) =>
                `relative text-base font-medium transition-colors duration-300 ${
                  isActive
                    ? 'text-coral-500'
                    : 'text-gray-700 hover:text-coral-500'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  About Us
                  {isActive && (
                    <span className="absolute -bottom-2 left-0 right-0 h-0.5 bg-coral-500 rounded-full animate-slideIn" />
                  )}
                </>
              )}
            </NavLink>

            <NavLink
              to="/gallery"
              className={({ isActive }) =>
                `relative text-base font-medium transition-colors duration-300 ${
                  isActive
                    ? 'text-coral-500'
                    : 'text-gray-700 hover:text-coral-500'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  Gallery
                  {isActive && (
                    <span className="absolute -bottom-2 left-0 right-0 h-0.5 bg-coral-500 rounded-full animate-slideIn" />
                  )}
                </>
              )}
            </NavLink>

            <NavLink
              to="/menu"
              className={({ isActive }) =>
                `relative text-base font-medium transition-colors duration-300 ${
                  isActive
                    ? 'text-coral-500'
                    : 'text-gray-700 hover:text-coral-500'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  Menu
                  {isActive && (
                    <span className="absolute -bottom-2 left-0 right-0 h-0.5 bg-coral-500 rounded-full animate-slideIn" />
                  )}
                </>
              )}
            </NavLink>

            <NavLink
              to="/cupcakes"
              className={({ isActive }) =>
                `relative text-base font-medium transition-colors duration-300 ${
                  isActive
                    ? 'text-coral-500'
                    : 'text-gray-700 hover:text-coral-500'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  Cupcakes
                  {isActive && (
                    <span className="absolute -bottom-2 left-0 right-0 h-0.5 bg-coral-500 rounded-full animate-slideIn" />
                  )}
                </>
              )}
            </NavLink>

            <NavLink
              to="/contact"
              className={({ isActive }) =>
                `relative text-base font-medium transition-colors duration-300 ${
                  isActive
                    ? 'text-coral-500'
                    : 'text-gray-700 hover:text-coral-500'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  Contact
                  {isActive && (
                    <span className="absolute -bottom-2 left-0 right-0 h-0.5 bg-coral-500 rounded-full animate-slideIn" />
                  )}
                </>
              )}
            </NavLink>

            <NavLink
              to="/faq"
              className={({ isActive }) =>
                `relative text-base font-medium transition-colors duration-300 ${
                  isActive
                    ? 'text-coral-500'
                    : 'text-gray-700 hover:text-coral-500'
                }`
              }
            >
              {({ isActive}) => (
                <>
                  FAQ
                  {isActive && (
                    <span className="absolute -bottom-2 left-0 right-0 h-0.5 bg-coral-500 rounded-full animate-slideIn" />
                  )}
                </>
              )}
            </NavLink>

            <NavLink
              to="/blog"
              className={({ isActive }) =>
                `relative text-base font-medium transition-colors duration-300 ${
                  isActive
                    ? 'text-coral-500'
                    : 'text-gray-700 hover:text-coral-500'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  Blog
                  {isActive && (
                    <span className="absolute -bottom-2 left-0 right-0 h-0.5 bg-coral-500 rounded-full animate-slideIn" />
                  )}
                </>
              )}
            </NavLink>

            <NavLink
              to="/cake-builder"
              className="btn-primary shadow-md hover:shadow-xl transform hover:scale-105 transition-all duration-300"
            >
              Order Now
            </NavLink>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMobileMenu}
            className="md:hidden relative w-10 h-10 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-coral-500 rounded-lg"
            aria-label="Toggle menu"
          >
            <div className="w-6 h-5 flex flex-col justify-between">
              <span
                className={`block h-0.5 bg-gray-700 rounded-full transition-all duration-300 ${
                  isMobileMenuOpen ? 'rotate-45 translate-y-2' : ''
                }`}
              />
              <span
                className={`block h-0.5 bg-gray-700 rounded-full transition-all duration-300 ${
                  isMobileMenuOpen ? 'opacity-0' : ''
                }`}
              />
              <span
                className={`block h-0.5 bg-gray-700 rounded-full transition-all duration-300 ${
                  isMobileMenuOpen ? '-rotate-45 -translate-y-2' : ''
                }`}
              />
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      <div
        className={`md:hidden transition-all duration-300 ease-in-out overflow-hidden ${
          isMobileMenuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <nav className="container mx-auto px-4 py-6 bg-white border-t border-gray-100">
          <div className="flex flex-col gap-4">
            <NavLink
              to="/"
              onClick={closeMobileMenu}
              className={({ isActive }) =>
                `text-lg font-medium py-2 px-4 rounded-lg transition-all duration-300 ${
                  isActive
                    ? 'bg-coral-50 text-coral-500'
                    : 'text-gray-700 hover:bg-gray-50'
                }`
              }
            >
              Home
            </NavLink>

            <NavLink
              to="/about"
              onClick={closeMobileMenu}
              className={({ isActive }) =>
                `text-lg font-medium py-2 px-4 rounded-lg transition-all duration-300 ${
                  isActive
                    ? 'bg-coral-50 text-coral-500'
                    : 'text-gray-700 hover:bg-gray-50'
                }`
              }
            >
              About Us
            </NavLink>

            <NavLink
              to="/gallery"
              onClick={closeMobileMenu}
              className={({ isActive }) =>
                `text-lg font-medium py-2 px-4 rounded-lg transition-all duration-300 ${
                  isActive
                    ? 'bg-coral-50 text-coral-500'
                    : 'text-gray-700 hover:bg-gray-50'
                }`
              }
            >
              Gallery
            </NavLink>

            <NavLink
              to="/menu"
              onClick={closeMobileMenu}
              className={({ isActive }) =>
                `text-lg font-medium py-2 px-4 rounded-lg transition-all duration-300 ${
                  isActive
                    ? 'bg-coral-50 text-coral-500'
                    : 'text-gray-700 hover:bg-gray-50'
                }`
              }
            >
              Menu
            </NavLink>

            <NavLink
              to="/cupcakes"
              onClick={closeMobileMenu}
              className={({ isActive }) =>
                `text-lg font-medium py-2 px-4 rounded-lg transition-all duration-300 ${
                  isActive
                    ? 'bg-coral-50 text-coral-500'
                    : 'text-gray-700 hover:bg-gray-50'
                }`
              }
            >
              Cupcakes
            </NavLink>

            <NavLink
              to="/contact"
              onClick={closeMobileMenu}
              className={({ isActive }) =>
                `text-lg font-medium py-2 px-4 rounded-lg transition-all duration-300 ${
                  isActive
                    ? 'bg-coral-50 text-coral-500'
                    : 'text-gray-700 hover:bg-gray-50'
                }`
              }
            >
              Contact
            </NavLink>

            <NavLink
              to="/faq"
              onClick={closeMobileMenu}
              className={({ isActive }) =>
                `text-lg font-medium py-2 px-4 rounded-lg transition-all duration-300 ${
                  isActive
                    ? 'bg-coral-50 text-coral-500'
                    : 'text-gray-700 hover:bg-gray-50'
                }`
              }
            >
              FAQ
            </NavLink>

            <NavLink
              to="/blog"
              onClick={closeMobileMenu}
              className={({ isActive }) =>
                `text-lg font-medium py-2 px-4 rounded-lg transition-all duration-300 ${
                  isActive
                    ? 'bg-coral-50 text-coral-500'
                    : 'text-gray-700 hover:bg-gray-50'
                }`
              }
            >
              Blog
            </NavLink>

            <NavLink
              to="/cake-builder"
              onClick={closeMobileMenu}
              className="btn-primary mt-2 text-center shadow-md"
            >
              Order Now
            </NavLink>
          </div>
        </nav>
      </div>

      <style jsx>{`
        @keyframes slideIn {
          from {
            transform: scaleX(0);
          }
          to {
            transform: scaleX(1);
          }
        }

        .animate-slideIn {
          animation: slideIn 0.3s ease-out;
        }
      `}</style>
    </header>
  )
}

export default Header
